import { useEffect, useState } from "react";
import { Flame, CalendarCheck, Beef, Trophy } from "lucide-react";
import { api } from "@/lib/api";

// AGT-06 "Your week in patterns" — a small retention card surfacing the
// longitudinal memory layer (streak, logging days, protein-goal days, best win).
export default function WeekInPatterns({ data }) {
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (data) return; // Dashboard already provides it — skip the duplicate request.
    let alive = true;
    const tz = new Date().getTimezoneOffset();
    api.get(`/insights/weekly?tz_offset=${tz}`).then((r) => { if (alive) setFetched(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [data]);

  const d = data || fetched;
  if (!d || !d.has_data) return null;

  const cells = [
    { key: "streak", icon: Flame, label: "Streak", value: `${d.streak_days}d`, color: "#FF6B47" },
    { key: "logged", icon: CalendarCheck, label: "Days logged", value: `${d.logging_days_7d}/7`, color: "#3A7D5D" },
  ];
  // Positive, clear third metric — only when a protein target exists (otherwise it'd
  // read a misleading 0/7). Replaces the old "late nights" metric, which measured when
  // a log was *created*, not when food was eaten.
  if (d.has_protein_target) {
    cells.push({ key: "protein", icon: Beef, label: "Protein goal", value: `${d.protein_days_hit}/7`, color: "#3A7D5D" });
  }

  return (
    <div className="mt-6 rounded-2xl border border-border bg-surface-elevated p-4" data-testid="week-in-patterns">
      <div className="text-overline mb-3">Your week in patterns</div>
      <div className="flex gap-2">
        {cells.map((c) => (
          <div key={c.key} className="flex-1 min-w-0 rounded-xl bg-surface border border-border px-3 py-2" data-testid={`week-stat-${c.key}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <c.icon size={13} style={{ color: c.color }} className="shrink-0" />
              <span className="text-[10px] uppercase tracking-tight font-semibold text-muted-foreground truncate">{c.label}</span>
            </div>
            <div className="text-lg font-semibold tabular-nums mt-0.5" style={{ color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>
      {d.best_win && (
        <div className="mt-3 flex items-center gap-2 text-sm text-foreground/80" data-testid="week-best-win">
          <Trophy size={15} className="text-secondary shrink-0" />
          <span>{d.best_win}</span>
        </div>
      )}
    </div>
  );
}
